import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

gsap.registerPlugin(ScrollToPlugin);

// FAST TRACK: Read language instantly when the script file loads, before waiting for DOMContentLoaded
const cookieLang = document.cookie
	.split("; ")
	.find((row) => row.startsWith("siteLang="));
const initialLang = cookieLang
	? cookieLang.split("=")[1]
	: localStorage.getItem("siteLang") || "en";

// Set both parameters cleanly out of the gate
document.documentElement.setAttribute("lang", initialLang);
document.documentElement.setAttribute("data-lang", initialLang);

class Navigation {
	constructor() {
		// Selectors
		this.header = document.querySelector("#header");
		this.menuIcon = document.querySelector("#header__menu-icon");
		this.menuOverlay = document.querySelector("#menu");
		this.donateButton = document.querySelector("#header__button");
		this.body = document.body;
		this.mobileLogo = document.querySelector("#header__mobile-logo");

		// Language Switcher (add below existing selectors)
		this.langOptions = document.querySelectorAll(".lang-switcher__option");
		this.desktopCurrent = document.querySelector("#current-lang-desktop");
		this.mobileCurrent = document.querySelector("#current-lang-mobile");

		this.currentLang = initialLang;

		this.applyLanguage(this.currentLang);
		document.documentElement.setAttribute("lang", this.currentLang);

		this.translateDonationForm(this.currentLang);

		this.desktopGlobe = document.querySelector(
			".lang-switcher--desktop .lang-switcher__icon",
		);

		// Scroll Tracking
		this.lastScrollY = 0;

		if (this.menuIcon && this.menuOverlay) {
			this.initMenuAnimation();
			this.events();
		}
	}

	initMenuAnimation() {
		this.menuTl = gsap.timeline({
			paused: true,
			defaults: { ease: "power3.inOut" },
		});

		this.menuTl
			.to(this.menuOverlay, {
				duration: 0.4,
				autoAlpha: 1,
			})
			.from(
				".menu__item, .menu__social, .menu__logo, .lang-switcher--mobile",
				{
					duration: 0.5,
					y: 100,
					opacity: 0,
					stagger: 0.1,
					clearProps: "transform",
				},
				"-=0.2",
			);
	}

	events() {
		this.menuIcon.addEventListener("click", () => this.toggleMenu());

		const allAnchorLinks = document.querySelectorAll('a[href*="#"]');
		allAnchorLinks.forEach((link) => {
			link.addEventListener("click", (e) => this.handleLinkClick(e));
		});

		document.addEventListener("keydown", (e) => {
			if (
				e.key === "Escape" &&
				this.menuOverlay.classList.contains("is-active")
			) {
				this.closeMenu();
			}
		});

		// Language switch events
		this.langOptions.forEach((btn) => {
			btn.addEventListener("click", (e) => {
				const selectedLang = e.currentTarget.dataset.lang;
				this.setLanguage(selectedLang);
			});
		});

		// Mobile language toggle
		const toggles = document.querySelectorAll(".lang-switcher__toggle");

		toggles.forEach((toggle) => {
			toggle.addEventListener("click", (e) => {
				e.stopPropagation();

				const parent = toggle.closest(".lang-switcher");
				parent.classList.toggle("is-open");
			});
		});

		// Close when clicking outside
		document.addEventListener("click", () => {
			document
				.querySelectorAll(".lang-switcher")
				.forEach((el) => el.classList.remove("is-open"));
		});

		document.querySelectorAll(".lang-switcher__list").forEach((list) => {
			list.addEventListener("click", (e) => {
				e.stopPropagation();
			});
		});

		window.addEventListener("scroll", () => this.handleScroll(window), {
			passive: true,
		});

		const formContainer = document.querySelector(".charitable-donation-form");
		if (formContainer) {
			this.isTranslating = false; // Flag to prevent infinite loops

			const observer = new MutationObserver((mutations) => {
				if (this.isTranslating) return; // Exit if the change came from our own script

				// Only trigger if nodes were added (likely an AJAX load)
				const hasNewNodes = mutations.some((m) => m.addedNodes.length > 0);
				if (hasNewNodes) {
					this.translateDonationForm(this.currentLang);
				}
			});

			observer.observe(formContainer, { childList: true, subtree: true });
		}
	}

	handleLinkClick(e) {
		const link = e.currentTarget;
		const href = link.getAttribute("href");

		if (href.includes("#") && !link.classList.contains("js-obfuscated-email")) {
			const url = new URL(link.href, window.location.origin);
			const targetId = url.hash;
			const isHomePage =
				window.location.pathname === "/" ||
				window.location.pathname.includes("index.php");
			const isMenuOpen = this.menuOverlay.classList.contains("is-active");

			if (isHomePage && document.querySelector(targetId)) {
				e.preventDefault();
				if (isMenuOpen) {
					this.closeMenu(() => this.scrollToSection(targetId));
				} else {
					this.scrollToSection(targetId);
				}
			} else if (isMenuOpen) {
				this.closeMenu();
			}
		}
	}

	scrollToSection(targetId) {
		gsap.to(window, {
			duration: 1.2,
			scrollTo: { y: targetId, autoKill: false, offsetY: 80 },
			ease: "power3.inOut",
		});
	}

	toggleMenu() {
		const isOpening = !this.menuOverlay.classList.contains("is-active");
		isOpening ? this.openMenu() : this.closeMenu();
	}

	openMenu() {
		this.menuIcon.classList.add("is-active");
		this.menuOverlay.classList.add("is-active");
		this.header.classList.add("is-active");
		this.body.style.overflow = "hidden";
		document.documentElement.style.overflow = "hidden";

		if (this.donateButton) {
			this.donateButton.style.opacity = "1";
			this.donateButton.style.visibility = "visible";
		}

		this.updateAssets(); // Updated call
		this.menuIcon.setAttribute("aria-expanded", "true");
		this.menuTl.play();
	}

	closeMenu(onCompleteCallback = null) {
		this.menuTl.reverse();
		this.menuTl.eventCallback("onReverseComplete", () => {
			this.menuIcon.classList.remove("is-active");
			this.menuOverlay.classList.remove("is-active");
			this.header.classList.remove("is-active");
			this.body.style.overflow = "";
			document.documentElement.style.overflow = "";

			if (this.donateButton) {
				this.donateButton.style.opacity = "";
				this.donateButton.style.visibility = "";
			}

			this.updateAssets(); // Updated call
			this.menuIcon.setAttribute("aria-expanded", "false");
			if (onCompleteCallback) onCompleteCallback();
		});
	}

	handleScroll(target) {
		const currentScrollY =
			target === window ? window.scrollY : target.scrollTop;
		const isMenuOpen = this.menuOverlay.classList.contains("is-active");

		if (currentScrollY > this.lastScrollY && currentScrollY > 50) {
			if (!isMenuOpen) this.header.classList.add("header--hidden");
		} else if (currentScrollY < this.lastScrollY) {
			this.header.classList.remove("header--hidden");

			// Update scrolled class based on position or menu state
			const shouldBeScrolled = currentScrollY > 0 || isMenuOpen;
			this.header.classList.toggle("header--scrolled", shouldBeScrolled);

			// Trigger the asset swap whenever the scroll state changes
			this.updateAssets();
		}
		this.lastScrollY = currentScrollY;
	}

	/**
	 * Refined updateAssets
	 * Now checks both menu status AND scroll status
	 */
	updateAssets() {
		if (typeof btsData === "undefined") return;

		const isMenuOpen = this.menuOverlay.classList.contains("is-active");
		const isHeaderScrolled = this.header.classList.contains("header--scrolled");

		// 1. Handle Mobile Logo
		if (this.mobileLogo) {
			this.mobileLogo.src = isMenuOpen
				? btsData.logoMobileAlt
				: btsData.logoMobile;
		}

		// 2. Handle Desktop Language Switcher
		if (this.desktopGlobe && this.desktopCurrent) {
			// It should be BLACK if the menu is open OR if the user has scrolled down
			const useBlackVersion = isMenuOpen || isHeaderScrolled;

			this.desktopGlobe.src = useBlackVersion
				? btsData.globeBlack
				: btsData.globeWhite;
			this.desktopCurrent.style.color = useBlackVersion ? "#000000" : "#ffffff";
		}
	}

	setLanguage(lang) {
		this.currentLang = lang;

		// 1. Save for the Client-side (JS)
		localStorage.setItem("siteLang", lang);

		// 2. Save for the Server-side (PHP)
		// IMPORTANT: 'path=/' ensures the cookie is available site-wide
		document.cookie = `siteLang=${lang}; path=/; max-age=31536000; SameSite=Lax`;

		// 3. Update the UI
		document.documentElement.setAttribute("lang", lang);
		document.documentElement.setAttribute("data-lang", lang);

		// donation form
		this.translateDonationForm(lang);
		this.applyLanguage(lang);

		// Close dropdowns
		document
			.querySelectorAll(".lang-switcher")
			.forEach((el) => el.classList.remove("is-open"));

		window.dispatchEvent(new CustomEvent("languageChange", { detail: lang }));
	}

	applyLanguage(lang) {
		// Update HTML lang attribute
		document.documentElement.setAttribute("lang", lang);

		const label = lang === "es" ? "ES" : "EN";

		// Desktop
		if (this.desktopCurrent) {
			this.desktopCurrent.textContent = label;
		}

		// Mobile
		if (this.mobileCurrent) {
			this.mobileCurrent.textContent = label;
		}
	}

	// translate text content for the donation form
	translateDonationForm(lang) {
		const form = document.querySelector(".charitable-donation-form");
		if (!form || this.isTranslating) return;

		this.isTranslating = true;

		// 1. Map the EXACT 'for' attributes from your inspector
		const labels = {
			charitable_field_first_name_element: {
				en: "First name",
				es: "Nombre de pila",
			},
			charitable_field_last_name_element: { en: "Last name", es: "Apellido" },
			charitable_field_email_element: {
				en: "Email Address",
				es: "Correo Electrónico",
			},
			charitable_field_cc_name_element: {
				en: "Name on Card",
				es: "Nombre en la Tarjeta",
			},
		};

		setTimeout(() => {
			this.isTranslating = false;
		}, 100);

		Object.keys(labels).forEach((id) => {
			const labelEl = form.querySelector(`label[for="${id}"]`);
			if (labelEl) {
				// Keep the asterisk if it exists
				const hasAsterisk = labelEl.querySelector("abbr");
				labelEl.textContent = labels[id][lang];
				if (hasAsterisk) {
					labelEl.innerHTML +=
						' <abbr class="required" title="Required">*</abbr>';
				}
			}
		});

		// 2. Translate "Donation Options" main label
		const optionsLabel = form.querySelector(
			".charitable-field-label-donation-amounts",
		);
		if (optionsLabel) {
			optionsLabel.innerHTML =
				(lang === "es" ? "Opciones de Donación" : "Donation Options") +
				' <abbr class="required" title="Required">*</abbr>';
		}

		// 3. Translate "One Time" and "Monthly" toggles
		const oneTimeLabel = form.querySelector('label[for*="-once"]');
		if (oneTimeLabel)
			oneTimeLabel.childNodes[2].textContent =
				lang === "es" ? " Una vez" : " One Time";

		const monthlyLabel = form.querySelector('label[for*="-recurring"]');
		if (monthlyLabel)
			monthlyLabel.childNodes[2].textContent =
				lang === "es" ? " Mensual" : " Monthly";

		// 4. Translate the descriptions ($15, $25, etc.)
		// 4. Translate the descriptions ($15, $25, etc.)
		const donationDescriptions = [
			{
				en: "Sends a Child on a STEM Field Trip",
				es: "Envía a un niño a a explorar centros de ciencia de STEM",
				key: "STEM", // A unique word to identify this specific row
			},
			{
				en: "Sparks Learning with a Math Puzzle",
				es: "Despierta curiosidad con un rompecabezas matemático",
				key: "mat",
			},
			{
				en: "Provides one Houston Science Family Festival Kit",
				es: "Proporciona un paquete de tecnología para una familia",
				key: "fam",
			},
			{
				en: "Funds a Student Microscope or Telescope",
				es: "Regala un microscopio o telescopio a un estudiante",
				key: "micro",
			},
			{
				en: "Sponsors a LEGO SPIKE Prime Robotics Set",
				es: "Proporciona un set de robótica LEGO SPIKE Prime a un equipo",
				key: "LEGO",
			},
		];

		form.querySelectorAll(".description").forEach((descElement) => {
			const currentText = descElement.textContent.toLowerCase();

			donationDescriptions.forEach((item) => {
				// We check if the element's current text contains our unique 'key'
				// This works whether the text is currently English or Spanish
				if (currentText.includes(item.key.toLowerCase())) {
					descElement.textContent = item[lang];
				}
			});
		});

		// 5. Translate Credit Card Label
		const ccLabel = form.querySelector(
			'label[for="charitable_stripe_card_field"]',
		);
		if (ccLabel)
			ccLabel.textContent =
				lang === "es" ? "Tarjeta de Crédito/Débito" : "Credit/Debit Card";

		// 6. Translate Submit Button
		const donateBtn = form.querySelector(".charitable-donate-button-submit");
		if (donateBtn) {
			donateBtn.textContent = lang === "es" ? "Donar" : "Donate";
		}
	}
}

window.addEventListener("DOMContentLoaded", () => {
	new Navigation();
});
